import _ from 'underscore';

import { Messages } from '../../codegen/Localize';
import { FeatureFlags } from '../../commonInfra/featureFlags/FeatureFlags';
import { AuthorizableType } from './AuthorizableType';

export enum Capabilities {
  addComment = 'add_comment',
  aiAccess = 'ai_access',
  changeHierarchy = 'change_hierarchy',
  changePermissions = 'change_permissions',
  connect = 'connect',
  deleteItem = 'delete',
  editPublishedConnection = 'edit_published_connection',
  execute = 'execute',
  explainData = 'explain_data',
  exportData = 'export_data',
  exportImage = 'export_image',
  exportXml = 'export_xml',
  filter = 'filter',
  inheritedProjectLeader = 'inherited_project_leader', // This is a read-only capability, not shown in the permissions data grid
  projectLeader = 'project_leader',
  read = 'read',
  shareView = 'share_view',
  viewComments = 'view_comments',
  vizqlDataApiAccess = 'vizql_data_api_access',
  vud = 'vud',
  webAuthoring = 'web_authoring',
  extractRefresh = 'allow_extract_refresh',
  webAuthoringForFlows = 'web_authoring_for_flows',
  write = 'write',
  xmlSaveAs = 'xml_save_as',
  pulseMetricDefine = 'pulse_metric_define',
  pulseMetricCertify = 'pulse_metric_certify',
}

export interface ICapabilityInfo {
  message: () => string;
  name: string;
}

// Capability metadata. Column-header icons are rendered in PermissionsGridCells.capabilityIcon() via
// @tableau/icons components (switch on the CapabilityInfo member); this dict carries only message + name.
export const CapabilityInfo: _.Dictionary<ICapabilityInfo> = {
  addComment: {
    message: Messages.capability_add_comment,
    name: Capabilities.addComment
  },
  aiAccess: {
    message: Messages.capability_ai_access,
    name: Capabilities.aiAccess
  },
  changeHierarchy: {
    message: Messages.capability_change_hierarchy,
    name: Capabilities.changeHierarchy
  },
  changePermissions: {
    message: Messages.capability_change_permissions,
    name: Capabilities.changePermissions
  },
  connect: {
    message: Messages.capability_connect,
    name: Capabilities.connect
  },
  connectWorkbook: {
    message: Messages.capability_full_data_query,
    name: Capabilities.connect
  },
  delete: {
    message: Messages.capability_delete,
    name: Capabilities.deleteItem
  },
  editPublishedConnection: {
    message: Messages.capability_write_content,
    name: Capabilities.editPublishedConnection
  },
  executeFlow: {
    message: Messages.capability_execute_flow,
    name: Capabilities.execute
  },
  explainData: {
    message: Messages.capability_explain_data,
    name: Capabilities.explainData
  },
  exportData: {
    message: Messages.capability_export_data,
    name: Capabilities.exportData
  },
  exportImage: {
    message: Messages.capability_export_image,
    name: Capabilities.exportImage
  },
  exportXmlDatasource: {
    message: Messages.capability_export_xml_datasource,
    name: Capabilities.exportXml
  },
  exportXmlFlow: {
    message: Messages.capability_export_xml_flow,
    name: Capabilities.exportXml
  },
  exportXmlWorkbook: {
    message: Messages.capability_export_xml_workbook_save_copy,
    name: Capabilities.exportXml
  },
  extractRefresh: {
    message: Messages.capability_allow_extract_refresh,
    name: Capabilities.extractRefresh
  },
  filter: {
    message: Messages.capability_filter,
    name: Capabilities.filter
  },
  read: {
    message: Messages.capability_read,
    name: Capabilities.read
  },
  shareView: {
    message: Messages.capability_share_view,
    name: Capabilities.shareView
  },
  viewComments: {
    message: Messages.capability_view_comments,
    name: Capabilities.viewComments
  },
  vizqlDataApiAccess: {
    message: Messages.capability_vizql_data_api_access,
    name: Capabilities.vizqlDataApiAccess
  },
  vud: {
    message: Messages.capability_vud,
    name: Capabilities.vud
  },
  webAuthoring: {
    message: Messages.capability_web_authoring,
    name: Capabilities.webAuthoring
  },
  webAuthoringForFlows: {
    message: Messages.capability_web_authoring_for_flows,
    name: Capabilities.webAuthoringForFlows
  },
  write: {
    message: Messages.capability_write_content,
    name: Capabilities.write
  },
  writeProject: {
    message: Messages.capability_write_project,
    name: Capabilities.write
  },
  saveAsDatasource: {
    message: Messages.capability_xml_save_as,
    name: Capabilities.xmlSaveAs
  },
  definePulseMetric: {
    message: Messages.capability_pulse_metric_define,
    name: Capabilities.pulseMetricDefine
  },
  certifyPulseMetric: {
    message: Messages.capability_pulse_metric_certify,
    name: Capabilities.pulseMetricCertify
  },
};

const CollectionCapabilities = [
  Capabilities.read
];

const CatalogCapabilities = [
  Capabilities.changeHierarchy,
  Capabilities.changePermissions,
  Capabilities.read,
  Capabilities.write
];

const DatasourceCapabilities = (isOnlineEnvironment?: boolean, allowNonAdminRefreshExtracts?: boolean) => [
  Capabilities.changeHierarchy,
  Capabilities.changePermissions,
  Capabilities.connect,
  Capabilities.deleteItem,
  Capabilities.exportXml,
  Capabilities.read,
  Capabilities.write,
  Capabilities.xmlSaveAs,
  ...isOnlineEnvironment ? [Capabilities.pulseMetricDefine] : [],
  ...isOnlineEnvironment && FeatureFlags.ntbue_CHECK_METRIC_CERTIFY_CAPEnabled ? [Capabilities.pulseMetricCertify] : [],
  ...allowNonAdminRefreshExtracts ? [Capabilities.extractRefresh] : [],
  Capabilities.vizqlDataApiAccess
];

const FlowCapabilities = [
  Capabilities.changeHierarchy,
  Capabilities.changePermissions,
  Capabilities.deleteItem,
  Capabilities.execute,
  Capabilities.exportXml,
  Capabilities.read,
  Capabilities.webAuthoringForFlows,
  Capabilities.write
];

const PublishedConnectionCapabilities = [
  Capabilities.changeHierarchy,
  Capabilities.changePermissions,
  Capabilities.connect,
  Capabilities.deleteItem,
  Capabilities.editPublishedConnection,
  Capabilities.read,
];

const ProjectCapabilities = [
  Capabilities.read,
  Capabilities.write
];

const ViewCapabilities = (
  analyticsAssistantDashboardQAEnabled?: boolean,
  einsteinCopilotForTableauEnabled?: boolean,
) => [
  Capabilities.addComment,
  ...analyticsAssistantDashboardQAEnabled &&
  einsteinCopilotForTableauEnabled
    ? [Capabilities.aiAccess]
    : [],
  Capabilities.changePermissions,
  Capabilities.connect,
  Capabilities.deleteItem,
  Capabilities.exportData,
  Capabilities.exportImage,
  Capabilities.filter,
  Capabilities.read,
  Capabilities.shareView,
  Capabilities.viewComments,
  Capabilities.vud,
  Capabilities.webAuthoring
];

const WorkbookCapabilities = (
  allowNonAdminRefreshExtracts?: boolean,
  analyticsAssistantDashboardQAEnabled?: boolean,
  einsteinCopilotForTableauEnabled?: boolean,
) => [
  Capabilities.addComment,
  ...analyticsAssistantDashboardQAEnabled &&
  einsteinCopilotForTableauEnabled
    ? [Capabilities.aiAccess]
    : [],
  Capabilities.changeHierarchy,
  Capabilities.changePermissions,
  Capabilities.connect,
  Capabilities.deleteItem,
  Capabilities.explainData,
  Capabilities.exportData,
  Capabilities.exportImage,
  Capabilities.exportXml,
  Capabilities.filter,
  Capabilities.read,
  Capabilities.shareView,
  Capabilities.viewComments,
  Capabilities.vud,
  ...allowNonAdminRefreshExtracts ? [Capabilities.extractRefresh] : [],
  Capabilities.webAuthoring,
  Capabilities.write,
  Capabilities.vizqlDataApiAccess
];

export const CapabilitiesByAuthorizableType = (
  isOnlineEnvironment?: boolean,
  allowNonAdminRefreshExtracts?: boolean,
  analyticsAssistantDashboardQAEnabled?: boolean,
  einsteinCopilotForTableauEnabled?: boolean,
): _.Dictionary<Capabilities[]> => {
  return {
    [AuthorizableType.COLLECTION]: CollectionCapabilities,
    [AuthorizableType.DATABASE]: CatalogCapabilities,
    [AuthorizableType.DATABASE_TABLE_TEMPLATE]: CatalogCapabilities,
    [AuthorizableType.DATASOURCE]: DatasourceCapabilities(isOnlineEnvironment, allowNonAdminRefreshExtracts),
    [AuthorizableType.FLOW]: FlowCapabilities,
    [AuthorizableType.PUBLISHED_CONNECTION]: PublishedConnectionCapabilities,
    [AuthorizableType.PROJECT]: ProjectCapabilities,
    [AuthorizableType.PROJECT_DATABASE_TEMPLATE]: CatalogCapabilities,
    [AuthorizableType.PROJECT_DATASOURCE_TEMPLATE]: DatasourceCapabilities(isOnlineEnvironment, allowNonAdminRefreshExtracts),
    [AuthorizableType.PROJECT_FLOW_TEMPLATE]: FlowCapabilities,
    [AuthorizableType.PROJECT_PUBLISHED_CONNECTION_TEMPLATE]: PublishedConnectionCapabilities,
    [AuthorizableType.PROJECT_TABLE_TEMPLATE]: CatalogCapabilities,
    [AuthorizableType.PROJECT_WORKBOOK_TEMPLATE]: WorkbookCapabilities(
      allowNonAdminRefreshExtracts,
      analyticsAssistantDashboardQAEnabled,
      einsteinCopilotForTableauEnabled,
    ),
    [AuthorizableType.TABLE]: CatalogCapabilities,
    [AuthorizableType.VIEW]: ViewCapabilities(
      analyticsAssistantDashboardQAEnabled,
      einsteinCopilotForTableauEnabled,
    ),
    [AuthorizableType.WORKBOOK]: WorkbookCapabilities(
      allowNonAdminRefreshExtracts,
      analyticsAssistantDashboardQAEnabled,
      einsteinCopilotForTableauEnabled,
    ),
  };
};
